export function mulberry32(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value += 0x6d2b79f5;
    let result = value;
    result = Math.imul(result ^ (result >>> 15), result | 1);
    result ^= result + Math.imul(result ^ (result >>> 7), result | 61);
    return ((result ^ (result >>> 14)) >>> 0) / 4294967296;
  };
}

export function solvedBoard(gridSize: number): number[] {
  return Array.from({ length: gridSize * gridSize }, (_, index) => index);
}

export function neighborIndexes(blankIndex: number, gridSize: number): number[] {
  const row = Math.floor(blankIndex / gridSize);
  const column = blankIndex % gridSize;
  const neighbors: number[] = [];
  if (row > 0) neighbors.push(blankIndex - gridSize);
  if (row < gridSize - 1) neighbors.push(blankIndex + gridSize);
  if (column > 0) neighbors.push(blankIndex - 1);
  if (column < gridSize - 1) neighbors.push(blankIndex + 1);
  return neighbors;
}

export function moveTile(board: readonly number[], tileIndex: number, gridSize: number): number[] | null {
  const blankValue = gridSize * gridSize - 1;
  const blankIndex = board.indexOf(blankValue);
  if (!neighborIndexes(blankIndex, gridSize).includes(tileIndex)) return null;
  const next = [...board];
  [next[blankIndex], next[tileIndex]] = [next[tileIndex]!, next[blankIndex]!];
  return next;
}

export function isSolved(board: readonly number[]): boolean {
  return board.every((value, index) => value === index);
}

export function isSolvableSlidingBoard(board: readonly number[], gridSize: number): boolean {
  if (board.length !== gridSize * gridSize) return false;
  const blankValue = gridSize * gridSize - 1;
  const values = board.filter((value) => value !== blankValue);
  let inversions = 0;
  for (let left = 0; left < values.length; left += 1) {
    for (let right = left + 1; right < values.length; right += 1) {
      if ((values[left] ?? 0) > (values[right] ?? 0)) inversions += 1;
    }
  }
  if (gridSize % 2 === 1) return inversions % 2 === 0;
  const blankIndex = board.indexOf(blankValue);
  if (blankIndex < 0) return false;
  const blankRowFromBottom = gridSize - Math.floor(blankIndex / gridSize);
  return (blankRowFromBottom % 2 === 0) !== (inversions % 2 === 0);
}

export function scrambleBoard(gridSize: number, moves: number, seed: number): number[] {
  const random = mulberry32(seed);
  let board = solvedBoard(gridSize);
  let previousBlank = -1;
  for (let step = 0; step < moves; step += 1) {
    const blankValue = gridSize * gridSize - 1;
    const blankIndex = board.indexOf(blankValue);
    const candidates = neighborIndexes(blankIndex, gridSize).filter((index) => index !== previousBlank);
    const choices = candidates.length > 0 ? candidates : neighborIndexes(blankIndex, gridSize);
    const tileIndex = choices[Math.floor(random() * choices.length)] ?? choices[0];
    if (tileIndex === undefined) break;
    previousBlank = blankIndex;
    board = moveTile(board, tileIndex, gridSize) ?? board;
  }
  if (isSolved(board)) {
    const blankIndex = board.indexOf(gridSize * gridSize - 1);
    const firstNeighbor = neighborIndexes(blankIndex, gridSize)[0];
    if (firstNeighbor !== undefined) board = moveTile(board, firstNeighbor, gridSize) ?? board;
  }
  return board;
}

export function swapEvidence(order: readonly string[], fromIndex: number, toIndex: number): string[] {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= order.length || toIndex >= order.length) {
    return [...order];
  }
  const next = [...order];
  [next[fromIndex], next[toIndex]] = [next[toIndex]!, next[fromIndex]!];
  return next;
}

export function ordersMatch(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

export function swapOrder<T>(order: readonly T[], fromIndex: number, toIndex: number): T[] {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= order.length || toIndex >= order.length) {
    return [...order];
  }
  const next = [...order];
  [next[fromIndex], next[toIndex]] = [next[toIndex]!, next[fromIndex]!];
  return next;
}

export function isNumericOrderSolved(order: readonly number[]): boolean {
  return order.every((value, index) => value === index);
}
